import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  ShippingCrossBorderFlatTeamSettingGroupById,
  type ShippingCrossBorderFlatTeamSettingGroupId,
} from "./groups";

type ShippingCrossBorderSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: ShippingCrossBorderSettingInput,
): ShippingCrossBorderSettingInput {
  return metadata;
}

function shippingCrossBorderGroup<
  const Group extends ShippingCrossBorderFlatTeamSettingGroupId,
  const T extends Record<string, ShippingCrossBorderSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys are real FlatTeamSettingsSchema keys (team.settings.* path, dots→slashes).
// purchaseLabelsOutsideOms is owned by the landedCost flat schema and reused here.
const ShippingCrossBorderRegistry = {
  ...shippingCrossBorderGroup("shipping_cross_border.cross_border", {
    "landedCost/purchaseLabelsOutsideOms": setting({
      label: "Purchase labels outside of Redo OMS",
      description:
        "Enable the Zonos UPS Unlock workflow so landed cost is guaranteed when shipping labels are purchased outside Redo's OMS (e.g. via ShipStation).",
      synonyms: [
        "purchase labels outside oms",
        "zonos ups unlock",
        "ups unlock",
        "labels outside redo oms",
        "shipstation labels",
      ],
    }),
    "landedCost/showCustomsSettings": setting({
      label: "Show customs settings to the merchant",
      description:
        "Shows the customs code and metafield settings in a collapsible section at the bottom of the merchant's International settings page. Redo staff turn this on in the admin Landed cost settings.",
      synonyms: ["customs settings visibility", "show customs settings"],
      visibility: "admin",
    }),
  }),
};

type ShippingCrossBorderFlatTeamSettingKey =
  keyof typeof ShippingCrossBorderRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ShippingCrossBorderFlatTeamSettingKeys = Object.keys(
  ShippingCrossBorderRegistry,
) as ShippingCrossBorderFlatTeamSettingKey[];

export const ShippingCrossBorderFlatTeamSettingCatalogDefinitions =
  ShippingCrossBorderFlatTeamSettingKeys.map((key) => {
    const definition = ShippingCrossBorderRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: ShippingCrossBorderFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    ShippingCrossBorderFlatTeamSettingKey,
    ShippingCrossBorderFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<ShippingCrossBorderFlatTeamSettingKey>
  >[];

export const ShippingCrossBorderFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    ShippingCrossBorderFlatTeamSettingCatalogDefinitions.map((definition) => [
      definition.id,
      definition.metadata,
    ]),
  ) as Record<ShippingCrossBorderFlatTeamSettingKey, SettingMetadata>;
